import React, {
    useState,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import {
    Card,
    CardBody,
    FormGroup,
    Row,
    Col,
    Button,
    Label,
    Input,
    Tooltip
} from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import Spinner from '../../common/Spinner';
import ConsoleLog from '../../common/ConsoleLog';
import { QuestionAnswerContext } from './QuestionAnswerContext';
import ProductNoteHtml from './ProductNoteHtml';

import CategoriesDropdown from './CategoriesDropdown';
import ProductsDropdown from './ProductsDropdown';
import Question from './Question';
import AddtoCartButton from './AddToCartButton';
import EvaluateCondition from './EvaluateCondition';
import { calculatePrice } from './PriceCalculator';
import TitleHeader from '../../common/TitleHeader';
import { isEmpty,evaluateMathOperation } from '../../lib/utils';
import MatrixLookup from './MatrixLookup';
import { Carousel } from 'react-bootstrap';
import { getParamPreservePlus } from '../../lib/utils';

const PriceElement = ({price, show, currency}) => {
    
    if(show)
    {
        
        if(price === "TBD")
            return (<span>TBD</span>);
        else{
            if(currency === "CAD"){
                return (<span>{ `C$${parseFloat(price).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}</span>);
            }
            else{
                return (<span>{ `$${parseFloat(price).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}</span>);
            }
          }
    }
    return (<></>);
}

const normalizePricingFlags = (pricing) => {
    const normalizedPricing = Array.isArray(pricing)
        ? pricing
        : String(pricing ?? '')
            .replace(/[\[\]"]/g, '')
            .split(',');

    return [0, 1, 2].map((index) => {
        const parsed = Number.parseInt(String(normalizedPricing[index] ?? '0').trim(), 10);
        return Number.isFinite(parsed) && parsed === 1 ? 1 : 0;
    });
};

const getPricingRuleState = (pricing) => {
    const pf = normalizePricingFlags(pricing);
    const isMsrpOnly = pf[0] === 1 && pf[1] === 0 && pf[2] === 0;
    const isWholesaleOnly = pf[1] === 1 && pf[2] === 0;
    const isMyPrice = pf[2] === 1;

    return { pf, isMsrpOnly, isWholesaleOnly, isMyPrice };
};

const buildPricingOutput = (pricingModel, pricing, product) => {
    const { isMsrpOnly, isWholesaleOnly, isMyPrice } = getPricingRuleState(pricing);
    const effectiveDfi = isMyPrice ? (product?.dfi ?? 0) : 0;
    const sourcePrice = Number(pricingModel?.price) || 0;

    const applyPricingFlags = (value, applyDfiThreshold) => {
        let adjustedValue = Number(value) || 0;
        if (isMsrpOnly) {
            adjustedValue = adjustedValue * 2;
        }
        if (isMyPrice && (!applyDfiThreshold || adjustedValue > 0.01)) {
            adjustedValue = adjustedValue - (adjustedValue * effectiveDfi);
        }
        if (isWholesaleOnly) {
            adjustedValue = Number(value) || 0;
        }
        return adjustedValue;
    };

    const configuredPrice = applyPricingFlags(sourcePrice, false);

    const configuredTotalDiscount = isMyPrice ? (sourcePrice * effectiveDfi) : 0;
    const adjustedUpcharges = (pricingModel?.upcharges || []).map((u) => {
        const value = Number(u.amount) || 0;
        const adjusted = applyPricingFlags(value, true);
        return {
            ...u,
            description: u.description === 'basePrice' ? 'BASE PRICE' : u.description,
            amount: adjusted
        };
    });

    return {
        configuredPrice,
        configuredWholesalePrice: sourcePrice,
        configuredTotalDiscount,
        adjustedUpcharges
    };
};

function resolveActionOperand(operand, answers, matrixData, useMatrix) {
    if (useMatrix) {
        return MatrixLookup(operand, answers, matrixData, false, 0);
    }
    if (!isNaN(operand)) {
        return Number(operand);
    }
    if (Object.prototype.hasOwnProperty.call(answers, operand)) {
        return answers[operand];
    }
    return operand;
}

function resolveActionAssignmentValue(valueToAssign, answers, isDirectReference) {
    if (isEmpty(valueToAssign)) {
        return valueToAssign;
    }
    const token = String(valueToAssign);
    if (isDirectReference && Object.prototype.hasOwnProperty.call(answers, token)) {
        return answers[token];
    }
    return token;
}

const ACTION_MATH_OPERATORS = ['+', '-', '*', '/'];

function isActionCalculation(action) {
    return action.isMath || ACTION_MATH_OPERATORS.includes(action.conditionCompOperator);
}

// Materialize GV/OV action variables. Multi-pass so GVs compute before dependents
// (e.g. WIDTH_DISP := GV_TV_WIDTH) and defaults can feed back into derived values.
function processActionVariables(actionVars, answers, matrixData, configGroup, qty) {
    if (!actionVars || actionVars.length === 0) {
        return answers;
    }

    const sortedActionVars = [...actionVars].sort((a, b) => {
        if (a.variableType === 'GV' && b.variableType !== 'GV') return -1;
        if (a.variableType !== 'GV' && b.variableType === 'GV') return 1;
        return 0;
    });

    const conditionMetMap = new Map();
    const maxPasses = sortedActionVars.length + 1;

    for (let pass = 0; pass < maxPasses; pass++) {
        let changed = false;

        sortedActionVars.forEach((actionVar) => {
            const { variable, conditions, actions } = actionVar;
            if (!conditionMetMap.has(variable)) {
                conditionMetMap.set(variable, false);
            }

            const allConditionsMet = EvaluateCondition(
                conditions,
                answers,
                matrixData,
                configGroup,
                qty,
                false,
                true
            );

            if (!allConditionsMet) {
                return;
            }

            conditionMetMap.set(variable, true);
            let valueToAssign = '';
            let actionValueTtl = 0;
            let nextMathAction = '';
            let isDirectReference = false;
            let combinedResult = true;

            actions.forEach((action) => {
                const leftValue = resolveActionOperand(
                    action.conditionVariable,
                    answers,
                    matrixData,
                    action.matrix
                );
                const rightValue = resolveActionOperand(
                    action.conditionAnswer,
                    answers,
                    matrixData,
                    action.matrixAnswer
                );

                if (!isActionCalculation(action)) {
                    if (isEmpty(action.conditionCompOperator)) {
                        valueToAssign = action.conditionVariable;
                        isDirectReference = true;
                    } else {
                        const conditionMet = evaluateMathOperation(
                            leftValue,
                            rightValue,
                            action.conditionCompOperator,
                            false,
                            0,
                            true
                        );

                        if (action.conditionOperator === 'AND') {
                            combinedResult = combinedResult && conditionMet;
                        } else if (action.conditionOperator === 'OR') {
                            combinedResult = combinedResult || conditionMet;
                        }
                    }
                } else {
                    let temp = 0;
                    let effectiveLeftValue = leftValue;
                    if (isEmpty(effectiveLeftValue)) {
                        effectiveLeftValue = 0;
                    }

                    if (effectiveLeftValue && rightValue) {
                        temp = evaluateMathOperation(
                            effectiveLeftValue,
                            rightValue,
                            action.conditionCompOperator,
                            false,
                            0
                        );
                    } else {
                        temp = effectiveLeftValue;
                    }

                    if (!isEmpty(nextMathAction)) {
                        actionValueTtl = evaluateMathOperation(
                            actionValueTtl,
                            temp,
                            nextMathAction,
                            false,
                            0
                        );
                        actionValueTtl = parseFloat(actionValueTtl.toFixed(2));
                        valueToAssign = actionValueTtl.toString();
                    } else {
                        actionValueTtl = parseFloat(temp.toFixed(2));
                        valueToAssign = actionValueTtl.toString();
                    }

                    isDirectReference = false;
                    nextMathAction = action.conditionOperator;
                }
            });

            if (!isEmpty(valueToAssign)) {
                const resolved = resolveActionAssignmentValue(
                    valueToAssign,
                    answers,
                    isDirectReference
                );
                if (answers[variable] !== resolved) {
                    answers[variable] = resolved;
                    changed = true;
                }
            }
        });

        if (!changed) {
            break;
        }
    }

    conditionMetMap.forEach((met, variable) => {
        if (!met) {
            delete answers[variable];
        }
    });
    return answers;
}

// Resolve currently-allowed options for a question (constrained options first).
function getVisibleOptionsForQuestion(questionObj, conditionEvaluations) {
    let visibleOptions = [];

    if (questionObj.constrainedOptions2 && questionObj.constrainedOptions2.length > 0) {
        for (const constraintGroup of questionObj.constrainedOptions2) {
            const conditionsMet = (constraintGroup.conditions || []).every((condition) =>
                conditionEvaluations[condition.conditionName] === true
            );
            if (conditionsMet) {
                visibleOptions = constraintGroup.options || [];
                break;
            }
        }
    }

    if (visibleOptions.length === 0 && questionObj.options) {
        visibleOptions = questionObj.options.filter((option) =>
            !option.conditions ||
            option.conditions.length === 0 ||
            (!isEmpty(option.conditionName) &&
                conditionEvaluations[option.conditionName] === true)
        );
    }

    return visibleOptions;
}

// If answers still hold an option value that is no longer visible (e.g. MIN_WIDTH
// after WDGE_SPEAKER_CFG changes), replace with default or the only available option.
function reconcileInvalidOptionAnswers(questions, answersObj, conditionEvaluations) {
    let changed = false;

    for (const questionObj of questions) {
        const variable = questionObj.question?.variable;
        if (!variable || isEmpty(answersObj[variable])) {
            continue;
        }

        const isVisible =
            !questionObj.conditions ||
            questionObj.conditions.length === 0 ||
            conditionEvaluations[questionObj.question.conditionName] === true;

        if (!isVisible) {
            continue;
        }

        const visibleOptions = getVisibleOptionsForQuestion(
            questionObj,
            conditionEvaluations
        );
        if (visibleOptions.length === 0) {
            continue;
        }

        const answerStillValid = visibleOptions.some(
            (opt) => opt.optionValue === answersObj[variable]
        );
        if (answerStillValid) {
            continue;
        }

        const replacement =
            visibleOptions.find((opt) => opt.isDefault) || visibleOptions[0];
        if (replacement && answersObj[variable] !== replacement.optionValue) {
            answersObj[variable] = replacement.optionValue;
            changed = true;
        }
    }

    return changed;
}

const QuestionsContainer = ({ queryParams, categories }) => {
    const NEW_PROJECT_ID = 0;
    const toNumericConfigId = (value) => {
        const parsed = Number.parseInt(String(value ?? '').trim(), 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    };
    // api url
    const api_url = process.env.REACT_APP_SERVER_URL;
     // store page loading state
    const [isLoading, setIsLoading] = useState(false);
    const [isGuest, setIsGuest] = useState(false);

    const [encryptedCustNo, setEncryptedCustNo] = useState("");
    const [encryptedWarehouse, setEncryptedWarehouse] = useState("");
    // store customer projects
    const [projects, setProjects] = useState([]);
    // store selected category
    const [selectedCategory, setSelectedCategory] = useState(undefined);
    const [dropDownCategories, setDropDownCategories] = useState(categories);
    // store project, title, quantity etc.
    const [title, setTitle] = useState('');
    const [selectedProjectOption, setSelectedProjectOption] = useState();
    const [notes, setNotes] = useState('');
    const [qty, setQty] = useState(1);
    const [isPrivate, setIsPrivate] = useState(false);
    // store selected productsetMyProductPrice
    const [selectedProduct, setSelectedProduct] = useState(undefined);
    // store questions for selected product
    const [questions, setQuestions] = useState([]);
    const [actionVars, setActionVars]  = useState([]);
    // store answer given by user for each question
    const [answers, setAnswers] = useState();
    // store the flag to determine if user has given a custom answer to any of the questions
    const [customAnswerGiven, setCustomAnswerGiven] = useState(false);
    //store calculated price
    const [showMyProductPrice, setShowMyProductPrice] = useState(false);
    const [myProductPrice, setMyProductPrice] = useState();
    const [configuredPrice, setConfiguredPrice] = useState();
    const [configuredNetPrice , setConfiguredNetPrice] = useState();
    const [configuredWholesalePrice, setConfiguredWholesalePrice] = useState(0);
    const [configuredTotalDiscount, setConfiguredTotalDiscount] = useState(0);
    const [showMSRPPrice, setShowMSRPPrice] = useState(true);
    const [msrpPrice, setMSRPPrice] = useState();
    const [showWholeSalePrice, setShowWholeSalePrice] = useState(true);
    const [wholesalePrice, setWholesalePrice] = useState();
    const [upcharges, setUpcharges] = useState([]);
    const [showUpcharges, setShowUpcharges] = useState(false);
    const [priceFormula, setPriceFormula] = useState([]);
    // store the conditions evaluation results based on the answer given
    const [conditionEvaluations, setConditionEvaluations] = useState({});
    const [readOnly, setReadOnly] = useState(false);

    //const { matrixData, priceFormula } = useContext(QuestionAnswerContext);
    const { matrixData } = useContext(QuestionAnswerContext);

    const [returnToDashboard, setReturnToDashboard] = useState(null);

    const [projectTooltipOpen , setProjectTooltipOpen ] = useState(false);
    const [projectZoneTooltipOpen , setProjectZoneTooltipOpen ] = useState(false);
    const toggleProjectTooltip = () => {
        setProjectTooltipOpen(!projectTooltipOpen);
    };

    const toggleProjectZoneTooltip = () => {
        setProjectZoneTooltipOpen(!projectZoneTooltipOpen);
    };


    // ref variables
    // store whether initial state is loaded or not
    const isInitialStateLoaded = useRef(false);
    // store config header
    const configHeader = useRef({
        customerNo: queryParams.customerNo ?? queryParams.customerNumber,
        email: queryParams.email,
        configId: toNumericConfigId(queryParams.configID),
        currency: queryParams.currency,
        warehouse: queryParams.warehouse,
        source: queryParams.source
    });

    const searchParam = useLocation().search;
    
    const ewh = getParamPreservePlus(searchParam, 'wh') ?? "kzGOKskISxJr0UtONySpd2jkxK7bMOumuda4EWucESo=";
    
   /* const ecn = encodeURIComponent(
        new URLSearchParams(searchParam).get('c')
    );*/
    const ecn = getParamPreservePlus(searchParam, 'c');
 
    // check if the question is mandatory or not
    const isQuestionRequired = (questionObj) => {
         // if condition present, give preference to condition. otherwise check isRequired on question
        const { conditionName } = questionObj.question;
        if (!isEmpty(conditionName) && Object.hasOwn(conditionEvaluations, conditionName) && conditionEvaluations[conditionName])
            return questionObj.conditions.some((cond) => cond.isRequired);
        else 
           return questionObj.question.isRequired;
         
    };

    // save product configuration
    const handleSaveButtonClick = async (e) => {
        e.preventDefault();
        // mandatory fields validation
        if (!selectedProjectOption) {
            toast.error('Please select Project');
            return false;
        }
        if (isEmpty(title)) {
            toast.error('Please enter Zone/Location');
            return false;
        }
        let url = "", api_method ="";
        if(questions && questions.length > 0)
        { 
            // mandatory questions validation
            for (const questionObj of questions) {
                
                // const required = isQuestionRequired(questionObj);
                // let isShown = (questionObj.conditions.length > 0  ? conditionEvaluations[questionObj.question.conditionName] : true);
                // if (required && !answers[questionObj.question.variable] && isShown)  {
                //     toast.error('Please answer the questions marked with *');
                //     return false;
                // }
                const variable = questionObj.question.variable;
                const isShown =
                  questionObj.conditions.length > 0
                    ? conditionEvaluations[questionObj.question.conditionName]
                    : true;

                    if (isShown && !answers?.[variable]) {
                        toast.error('Please answer all required questions.');
                        return false;
                    }
            }


            //insert check for validation rules
             let url = `${api_url}Product/Validation?pg=${selectedProduct.pageGenRule}`;
             let api_method = axios.get;
             const validationRuleResponse = await api_method(url, {
                headers: { 'Content-Type': 'application/json' },
            });
            var validationRules = validationRuleResponse.data;
            
            if(validationRules && validationRules.length > 0){
                for(const rule of validationRules) {
                    // Evaluate the conditions for this rule
                    debugger;
                    const isValid = EvaluateCondition(
                        rule.conditions,
                        answers,
                        matrixData,
                        selectedProduct.configGroup,
                        qty,
                        true //isVal
                    );
                    
                    // If rule conditions evaluate to true (violation matched), show error and abort save
                    if (isValid) {
                        toast.error(rule.errorMessage);
                        setIsLoading(false);
                        return false;
                    }
                                    
                    
                }
            }

            
        }

        try {
            setIsLoading(true);
            let { value: projectId } = selectedProjectOption;
            // check if new project is to be cretated
            if (projectId === NEW_PROJECT_ID) {
                const newProjectName = String(selectedProjectOption?.label ?? '').trim();
                if (!newProjectName) {
                    toast.error('Please enter a valid project name.');
                    return false;
                }
                
                const newProject = await createProject(
                    newProjectName
                );
                if (newProject == null) {
                    throw new Error('Error creating new project');
                }
                setProjects((prev) => [...prev, newProject]);
                setSelectedProjectOption({
                    label: newProject.projectName,
                    value: newProject.id,
                });
                projectId = newProject.id;
            }

            // save product configuration
            const newSessionDetails = [];
            
            for (const [condQuestion, condAnswer] of Object.entries(answers)) {
                const isGlobalVariable = actionVars.some(x => x.variableType === 'GV' && x.variable === condQuestion);
                if (!isGlobalVariable) {
                    newSessionDetails.push({
                        CondQuestion: condQuestion,
                        CondAnswer: condAnswer,
                    });
                }
            }
    
            // Build concatenated upcharges string "[Desc]: [Amount];"
           // const upchargesString = Array.isArray(upcharges) && upcharges.length > 0
           //     ? upcharges.map(u => `${u.description}: ${Number(u.amount).toFixed(2)}|`).join('')
           //    : '';
            let payload = {
                CustomerNumber: configHeader.current.customerNo,
                Price:  configuredPrice,
                idProject: projectId,
                CreatedBy : configHeader.current.email,
                Title: title,
                Answers: newSessionDetails,
                Qty: qty,
                Notes: notes,
                IsPrivate: isPrivate,
                PageGenRule: selectedProduct.pageGenRule,
                UpchargeList: upcharges,
                PricingFlag: queryParams.pricing,
                Currency: configHeader.current.currency,
                DFI: selectedProduct.dfi
            };
 
            
            // if new config or clone, save new, else edit existing config
            url = `${api_url}Product/SessionHeader`;
            api_method = axios.post;
            
            if (toNumericConfigId(configHeader.current.configId) > 0) {
                url = `${api_url}Product/UpdateProductConfiguration/${configHeader.current.configId}`;
                api_method = axios.put;
            }

            const apiResponse = await api_method(url, payload, {
                headers: { 'Content-Type': 'application/json' },
            });
            configHeader.current.configId = apiResponse.data.configID;
            

            /*------Add to cart-----*/
    
            if(e.target.id === "addToCart") {
                url = `${api_url}Product/GetProductConfigurationById/${configHeader.current.configId}/Clean`;
                api_method = axios.get;
    
                const apiResponse2 = await api_method(url, payload, {
                    headers: { 'Content-Type': 'application/json' },
                });
        
                var excludeVars =  process.env.REACT_APP_EXCLUDE_VARS_FROM_CART.split(',');

                var optiConfig = apiResponse2.data.sessionConfigurationDetails.filter(sd => !excludeVars.includes(sd.configDetailVariable)) .map(s => [s.configDetailVariable, s.answerText]);;
                                                                

              /*  if(configuredPrice === "TBD")
                {
                    url = `${api_url}Product/SendforReview/${configHeader.current.configId}`;
                    api_method = axios.put;
                    const apiResponse3 = await api_method(url, payload, {
                        headers: { 'Content-Type': 'application/json' },
                    });
                    
                }
                else*/
                if(queryParams.canAddToCart && optiConfig)
                {
        
                    //send message to parent window
                    var postMessage =  {
                        "Status": "Complete",
                        "ConfigurationId":   configHeader.current.configId,
                        "ConfigurationOptions": optiConfig,
                        "Product_Id": selectedProduct.productId ,//productID,
                        "Quantity":qty,
                        "Price": configuredPrice,
                        "Upcharges": upcharges
                    };
                    console.log("postMessage", postMessage);
                    window.parent.postMessage(postMessage, "*");
                }

                

            }
            if (returnToDashboard && configuredPrice !== "TBD") {
                
                
                // Small delay to allow the toast to be visible
                setTimeout(() => {
                    window.location.href = returnToDashboard;
                }, 1500);
                
               
            }
    
            

            toast.success('Configuration saved successfully.');
            return true;
        } catch (error) {
            console.log('error saving the product configuration', error);
            toast.error('Error saving the product configuration.');
        } finally {
            setIsLoading(false);
        }
    };

    // use memo for finding out the matrix variables on which the price calc is dependent.
    // This is cached function. Runs only hen Matrix data changes (if at all).
    const priceMatrixDependencies = useMemo(
        () => getPriceMatrixDependencies(matrixData),
        [matrixData]
    );

    function getPriceMatrixDependencies(matrixData) {
        let priceDependencyItems = new Set();
        for (let item of matrixData) {
            const { variable1, variable2, variable3, variable4, variable5 } =
                item.matrix;
            if (!isEmpty(variable1)) priceDependencyItems.add(variable1);
            if (!isEmpty(variable2)) priceDependencyItems.add(variable2);
            if (!isEmpty(variable3)) priceDependencyItems.add(variable3);
            if (!isEmpty(variable4)) priceDependencyItems.add(variable4);
            if (!isEmpty(variable5)) priceDependencyItems.add(variable5);
        }
        return priceDependencyItems;
    }

    // handle category change
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        //reset selected product, answers, quantity etc.
        setSelectedProduct(undefined);
        setAnswers({});
        setQty(1);
    };

    // handle product change
    const handleProductChange = useCallback(
        async (product) => {
            setSelectedProduct(product);
            setCustomAnswerGiven(false);
            if (product === undefined) return;
    
            setIsLoading(true);
            try {
                // set quantity and initial price
                setQty(1);
       
                getPriceFormula(product, configHeader.current.currency);
                checkPricingPermissions(queryParams.pricing, product, 0);
                
                // Set initial required answers
                let defaultAnswers = {
                    PRODUCT_ID: product.productId,
                    IV_CUST_CURRNCY: configHeader.current.currency,
                    BUSINESS_UNIT_IN: configHeader.current.warehouse,
                    IS_CONFIGURED_KIT: product.isConfigKit
                };
    
                // fetch questions for the product
                const pageGenModel = await getQuestions(product.pageGenRule, product.configGroup, product.productId);
                setQuestions(pageGenModel.questions);
                setActionVars(pageGenModel.actionVariables);
                if(pageGenModel.questions) {
                    // First evaluate initial conditions with just the required answers
                    const initialDependentConditions = getConditionsForQuestions(pageGenModel.questions);
                    let conditionsEvalResult = evaluateConditions(
                        initialDependentConditions,
                        defaultAnswers,
                        matrixData,
                        product.configGroup,
                        1 //qty
                    );
    
                    // Now process default answers only for visible questions
                    for (let questionObj of pageGenModel.questions) {
                        const isQuestionVisible = questionObj.conditions.length === 0 || 
                            conditionsEvalResult[questionObj.question.conditionName] === true;
    
                        if (isQuestionVisible && questionObj.defaultCondition) {
                            const { matrix, conditionVariable, conditionAnswer } = questionObj.defaultCondition;
                                
                            if (matrix) {
                                // Evaluate against matrix data
                                const matrixResult = evaluateMatrixDefault(
                                    questionObj.defaultCondition,
                                    defaultAnswers,
                                    matrixData,
                                    product.configGroup
                                );
                                          
                                if (matrixResult) {
                                    // Set the RTNValue as the default answer
                                    defaultAnswers[questionObj.question.variable] = matrixResult;
                                    
                                    // Mark the corresponding option as default
                                    const matchingOption = questionObj.options.find(
                                        opt => opt.optionValue === matrixResult
                                    );
                                    if (matchingOption) {
                                        matchingOption.isDefault = true;
                                    }
                                }
                            } else if (!matrix && conditionAnswer === '' && conditionVariable) {
                                // Direct default answer without matrix evaluation
                                defaultAnswers[questionObj.question.variable] = conditionVariable;
                                
                                // Mark the corresponding option as default
                                const matchingOption = questionObj.options.find(
                                    opt => opt.optionValue === conditionVariable
                                );
                                if (matchingOption) {
                                    matchingOption.isDefault = true;
                                }
                            }
                        }
                    }
    
                    // Re-evaluate conditions with all default answers
                    
                    conditionsEvalResult = evaluateConditions(
                        initialDependentConditions,
                        defaultAnswers,
                        matrixData,
                        product.configGroup,
                        1 //qty
                    );

                    processActionVariables(
                        pageGenModel.actionVariables,
                        defaultAnswers,
                        matrixData,
                        product.configGroup,
                        1
                    );

                    conditionsEvalResult = evaluateConditions(
                        initialDependentConditions,
                        defaultAnswers,
                        matrixData,
                        product.configGroup,
                        1 //qty
                    );
                    setAnswers(defaultAnswers);
                    setConditionEvaluations(conditionsEvalResult);
                } else {
                    setAnswers(defaultAnswers);
                }
                
            } catch (error) {
                console.error('Error in handleProductChange:', error);
                toast.error('Error loading product configuration');
            } finally {
                setIsLoading(false);
            }
        },
        [matrixData, queryParams.pricing]
    );

    // Helper function to evaluate matrix-based defaults
    const evaluateMatrixDefault = (defaultCondition, answers, matrixData, configGroup) => {
        if (!matrixData || !defaultCondition) return null;

        answers["IV_CONFIG_GRP"] = configGroup;
        answers["IV_CONFIG_GRP2"] = configGroup;
    

        answers["BUSINESS_UNIT_IN"] = answers["BUSINESS_UNIT_IN"].replace("002-","");
 
        // Find matching matrix record
        var matchingMatrix = MatrixLookup(defaultCondition.conditionVariable, answers, matrixData , false, 0);
    
        // Return RTNValue if found
        return matchingMatrix;
    };

    const getPriceFormula = async (product,curr) => {
        //selectedProduct, 
        console.log("product", product);
        try {
           
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}Product/GetPriceFormula?c=${product.pageGenRule}&p=${product.productId}&curr=${curr}`
            );
            const data = await response.json();
        
            setPriceFormula(data);
            return data;
        } catch (error) {
            console.log('error fetching price formula', error);
       }
       return null;
    };
             

    // Helper function to find dependent questions that should have their answers cleared
    const findDependentQuestions = (questions, changedVariable, newAnswers, conditionEvaluations) => {
        let dependentVariables = new Set();
        let processedVariables = new Set();
        
        const checkDependencies = (variable, simulatedAnswers, dependencyChain = new Set()) => {
            // Prevent infinite recursion
            if (dependencyChain.has(variable)) {
                console.warn('Circular dependency detected:', [...dependencyChain, variable].join(' -> '));
                return;
            }
            
            // Skip if already processed
            if (processedVariables.has(variable)) {
                return;
            }
            
            processedVariables.add(variable);
            dependencyChain.add(variable);
            
            // For each question, check if it depends on this variable
            for (let questionObj of questions) {
                const questionVariable = questionObj.question.variable;
                
                // Skip self-references
                if (questionVariable === variable) continue;
                
                // Check if this question depends on our variable (directly or indirectly)
                const isDependentQuestion = 
                    questionObj.conditions.some(cond => cond.conditionVariable === variable) ||
                    (questionObj.options && questionObj.options.some(opt => opt.optionConstraint === variable));

                if (isDependentQuestion) {
                    // Create a simulated answers object where this variable is marked for removal
                    const currentSimulatedAnswers = {
                        ...newAnswers,
                        ...simulatedAnswers
                    };
                    
                    // Evaluate if this question should be visible
                    const questionVisible = EvaluateCondition(
                        questionObj.conditions,
                        currentSimulatedAnswers,
                        matrixData,
                        selectedProduct.configGroup,
                        qty
                    );

                    // If question would be hidden OR the variable we're checking was just cleared
                    if (!questionVisible || simulatedAnswers[variable] === undefined) {
                        // Add this question's variable to the dependent list
                        if (!dependentVariables.has(questionVariable)) {
                            dependentVariables.add(questionVariable);
                            
                            // Continue the dependency chain with this question's answer cleared
                            const newSimulatedAnswers = {
                                ...simulatedAnswers,
                                [questionVariable]: undefined
                            };
                            
                            // Recursively check for questions dependent on this one
                            checkDependencies(questionVariable, newSimulatedAnswers, new Set(dependencyChain));
                        }
                    }
                }
            }
        };
        
        // Start with the initially changed variable
        const initialSimulatedAnswers = {
            [changedVariable]: newAnswers[changedVariable]
        };
        
        checkDependencies(changedVariable, initialSimulatedAnswers, new Set());
        
        return dependentVariables;
    };

    // handle answer selection
    const handleAnswer = (questionVariable, choiceValue) => {
        // Prevent duplicate updates
        if (answers[questionVariable] === choiceValue) {
            return;
        }

        const newAnswers = {
            ...answers,
            [questionVariable]: choiceValue,
        };
        let customAnswerFlag = false;
        let triggerPriceCalc = true;
        if (priceMatrixDependencies.has(questionVariable)) {
            triggerPriceCalc = true;
        }
 
        // Process action variables (GVs first, multi-pass for dependencies)
        processActionVariables(
            actionVars,
            newAnswers,
            matrixData,
            selectedProduct.configGroup,
            qty
        );

      
        // Get ALL conditions for evaluation
        const allConditions = getConditionsForQuestions(questions);
       
        // First evaluate all conditions with the new answer
        const conditionsEvalResult = evaluateConditions(
            allConditions,
            newAnswers,
            matrixData,
            selectedProduct.configGroup,
            qty
        );

        // Find all dependent questions that should have their answers cleared
        const dependentsToClear = findDependentQuestions(questions, questionVariable, newAnswers, conditionsEvalResult);
        
        // Clear answers for dependent questions that are now hidden
        dependentsToClear.forEach(variable => {
            delete newAnswers[variable];
            if (priceMatrixDependencies.has(variable)) {
                triggerPriceCalc = true;
            }
        });

        // Track processed questions to prevent loops
        const processedQuestions = new Set([questionVariable]);
        
        // Check for newly visible questions and set their default answers
        let hasNewDefaults = false;
        do {
            hasNewDefaults = false;
            for (let questionObj of questions) {
                const questionVar = questionObj.question.variable;
                
                // Skip already processed questions
                if (processedQuestions.has(questionVar)) continue;
                
                const wasVisible = questionObj.conditions.length === 0 || 
                    conditionEvaluations[questionObj.question.conditionName] === true;
                const isNowVisible = questionObj.conditions.length === 0 || 
                    conditionsEvalResult[questionObj.question.conditionName] === true;
                
                if (!wasVisible && isNowVisible && !newAnswers[questionVar]) {
                    const { matrix, conditionVariable, conditionAnswer } = questionObj.defaultCondition || {};
                    var matrixResult = "";
                    if (matrix) {
                        matrixResult = evaluateMatrixDefault(
                            questionObj.defaultCondition,
                            newAnswers,
                            matrixData,
                            selectedProduct.configGroup
                        );
                    }

                    let defaultWasSet = false;

                    // If options exist, try to select a default option as before
                    if (questionObj.options && questionObj.options.length > 0) {
                        const defaultOption = questionObj.options.find(opt =>
                            (!isEmpty(matrixResult) && opt.optionValue == matrixResult) ||
                            ((opt.conditions.length === 0 || conditionsEvalResult[opt.conditionName]) && opt.isDefault)
                        );
                        if (defaultOption) {
                            newAnswers[questionVar] = defaultOption.optionValue;
                            processedQuestions.add(questionVar);
                            hasNewDefaults = true;
                            defaultWasSet = true;

                            if (priceMatrixDependencies.has(questionVar)) {
                                triggerPriceCalc = true;
                            }
                        }
                    }

                    // If no options (text input) or no default option found, use defaultCondition to set a value
                    if (!defaultWasSet && questionObj.defaultCondition) {
                        // Prefer matrix result when available
                        if (matrix && !isEmpty(matrixResult)) {
                            newAnswers[questionVar] = matrixResult;
                            processedQuestions.add(questionVar);
                            hasNewDefaults = true;

                            if (priceMatrixDependencies.has(questionVar)) {
                                triggerPriceCalc = true;
                            }
                        } else if (!matrix && conditionAnswer === '' && conditionVariable) {
                            // Direct default value from conditionVariable (e.g., "0.000")
                            newAnswers[questionVar] = conditionVariable;
                            processedQuestions.add(questionVar);
                            hasNewDefaults = true;

                            if (priceMatrixDependencies.has(questionVar)) {
                                triggerPriceCalc = true;
                            }
                        }
                    }
                }
                processedQuestions.add(questionVar);
            }
        } while (hasNewDefaults);

        // Re-run action vars after defaults may have supplied source answers (e.g. OPTN_TV_WIDTH)
        processActionVariables(
            actionVars,
            newAnswers,
            matrixData,
            selectedProduct.configGroup,
            qty
        );
     
        // Re-evaluate all conditions one final time with any new default answers
        let finalConditionsEvalResult = evaluateConditions(
            allConditions,
            newAnswers,
            matrixData,
            selectedProduct.configGroup,
            qty,
            true
        );

        // When a driving answer changes (e.g. WDGE_SPEAKER_CFG), constrained
        // options for still-visible questions can change. Replace stale answers
        // so save payload matches what the UI shows (e.g. MIN_WIDTH).
        if (reconcileInvalidOptionAnswers(questions, newAnswers, finalConditionsEvalResult)) {
            finalConditionsEvalResult = evaluateConditions(
                allConditions,
                newAnswers,
                matrixData,
                selectedProduct.configGroup,
                qty,
                true
            );
        }
        setConditionEvaluations(finalConditionsEvalResult);

        // Check for custom answers in visible options
        for (let questionObj of questions) {
            const { variable } = questionObj.question;
            
            if (!isEmpty(newAnswers[variable]) && questionObj.options.length > 0) {
                const visibleOptions = getVisibleOptionsForQuestion(
                    questionObj,
                    finalConditionsEvalResult
                );

                if (isCustomAnswer(newAnswers[variable], visibleOptions)) {
                    customAnswerFlag = true;
                }
            }
        }

        setCustomAnswerGiven(customAnswerFlag);
        setAnswers(newAnswers);

        if (triggerPriceCalc) {
            const pricingModel = calculatePrice(
                priceFormula,
                matrixData,
                newAnswers,
                selectedProduct,
                qty,
                queryParams.pricing 
            );
            const {
                configuredPrice: nextConfiguredPrice,
                configuredWholesalePrice: nextConfiguredWholesalePrice,
                configuredTotalDiscount: nextConfiguredTotalDiscount,
                adjustedUpcharges
            } = buildPricingOutput(pricingModel, queryParams.pricing, selectedProduct);
            setUpcharges(adjustedUpcharges);

            if(!customAnswerFlag) {
                setConfiguredPrice(nextConfiguredPrice);
                setConfiguredNetPrice(nextConfiguredPrice * qty);
                setConfiguredWholesalePrice(nextConfiguredWholesalePrice);
                setConfiguredTotalDiscount(nextConfiguredTotalDiscount);
            } else {
                setConfiguredPrice("TBD");
                setConfiguredNetPrice("TBD");
                setConfiguredWholesalePrice(0);
                setConfiguredTotalDiscount(0);
            }
        }
    };

    // handle quantity change
    const handleQtyChange = (event) => {
        let newQty = event.target.value;
        
        if (!isNaN(newQty) && newQty > 0) {
            
            setQty(newQty);
            if (!customAnswerGiven && !isNaN(configuredPrice)) {
                const pricingModel = calculatePrice(
                    priceFormula,
                    matrixData,
                    answers,
                    selectedProduct,
                    newQty,
                    queryParams.pricing 
                );
                const {
                    configuredPrice: nextConfiguredPrice,
                    configuredWholesalePrice: nextConfiguredWholesalePrice,
                    configuredTotalDiscount: nextConfiguredTotalDiscount,
                    adjustedUpcharges
                } = buildPricingOutput(pricingModel, queryParams.pricing, selectedProduct);

                setUpcharges(adjustedUpcharges);
                setConfiguredPrice(nextConfiguredPrice);
                setConfiguredNetPrice(newQty * nextConfiguredPrice);
                setConfiguredWholesalePrice(nextConfiguredWholesalePrice);
                setConfiguredTotalDiscount(nextConfiguredTotalDiscount);
            }
        }
    };

    // handle project change
    const handleProjectChange = (newValue, action) => {
        if (action?.action === 'create-option') {
            const newProjectName = String(newValue?.label ?? '').trim();
            if (!newProjectName) {
                toast.error('Please enter a valid project name.');
                return;
            }
            setSelectedProjectOption({
                label: newProjectName,
                value: NEW_PROJECT_ID, // cue for creating new project
            });
        } else {
            setSelectedProjectOption(newValue);
        }
    };

    // function to fetch questions for given config model
    const getQuestions = async (pageGenRule, configGroup, productId) => {
        try {
            const apiResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}Product/QuestionsByProduct/${productId}/${pageGenRule}/${configGroup}/${configHeader.current.warehouse}`
            );
            
            if (!apiResponse.ok) {
                throw new Error(`HTTP error! status: ${apiResponse.status}`);
            }
            

            
            const data = await apiResponse.json();
           
            return data;
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error('Error getting questions list.');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Build answers object from API session details rows
    const buildAnswersFromSessionDetails = (sessionConfigurationDetails) => {
        const built = {};
        if (!sessionConfigurationDetails) return built;
        for (const item of sessionConfigurationDetails) {
            const { configDetailVariable, answerText } = item;
            built[configDetailVariable] = answerText;
        }
        return built;
    };

    // Apply provided answers: set state, evaluate conditions, hydrate defaults, compute price/upcharges
    const applyAnswersAndComputeState = async (product, providedAnswers, qtyValue, pricingFlags) => {
        // Start with a mutable copy
        const newAnswers = { ...(providedAnswers || {}) };
        setAnswers(newAnswers);

        // Ensure price formula is available
        let pfLocal = priceFormula;
        if (!pfLocal || pfLocal.length === 0) {
            pfLocal = await getPriceFormula(product, configHeader.current.currency);
        }

        // Fetch questions and action variables
        const pageGenModel = await getQuestions(product.pageGenRule, product.configGroup, product.productId);
        if (pageGenModel) {
            setQuestions(pageGenModel.questions);
            setActionVars(pageGenModel.actionVariables);

            // 1) Process actionVars to materialize derived variables (mirrors handleAnswer)
            processActionVariables(
                pageGenModel.actionVariables,
                newAnswers,
                matrixData,
                product.configGroup,
                qtyValue
            );

            // 2) Evaluate all conditions with the current answers
            const allConditions = getConditionsForQuestions(pageGenModel.questions);
            
            let conditionsEvalResult = evaluateConditions(
                allConditions,
                newAnswers,
                matrixData,
                product.configGroup,
                qtyValue,
                true
            );

            // 3) Hydrate defaults for newly visible questions (mirrors handleAnswer loop)
            const processedQuestions = new Set();
            let hasNewDefaults = false;
            do {
                hasNewDefaults = false;
                for (let questionObj of pageGenModel.questions) {
                    const questionVar = questionObj.question.variable;
                    if (processedQuestions.has(questionVar)) continue;
                    const wasVisible = questionObj.conditions.length === 0 || false; // initial rendering not needed here
                    const isNowVisible = questionObj.conditions.length === 0 || conditionsEvalResult[questionObj.question.conditionName] === true;
                    if (isNowVisible && isEmpty(newAnswers[questionVar])) {
                        const { matrix, conditionVariable, conditionAnswer } = questionObj.defaultCondition || {};
                        let matrixResult = "";
                        if (matrix) {
                            matrixResult = evaluateMatrixDefault(
                                questionObj.defaultCondition,
                                newAnswers,
                                matrixData,
                                product.configGroup
                            );
                        }
                        let defaultWasSet = false;
                        
                        if (questionObj.options && questionObj.options.length > 0) {
                            const defaultOption = questionObj.options.find(opt =>
                                (!isEmpty(matrixResult) && opt.optionValue == matrixResult) ||
                                ((opt.conditions.length === 0 || conditionsEvalResult[opt.conditionName]) && opt.isDefault)
                            );
                            if (defaultOption) {
                                newAnswers[questionVar] = defaultOption.optionValue;
                                processedQuestions.add(questionVar);
                                hasNewDefaults = true;
                                defaultWasSet = true;
                            }
                        }
                        if (!defaultWasSet && questionObj.defaultCondition) {
                            if (matrix && !isEmpty(matrixResult)) {
                                newAnswers[questionVar] = matrixResult;
                                processedQuestions.add(questionVar);
                                hasNewDefaults = true;
                            } else if (!matrix && conditionAnswer === '' && conditionVariable) {
                                newAnswers[questionVar] = conditionVariable;
                                processedQuestions.add(questionVar);
                                hasNewDefaults = true;
                            }
                        }
                    }
                    processedQuestions.add(questionVar);
                }
                // Re-evaluate conditions after setting any defaults
                if (hasNewDefaults) {
                    conditionsEvalResult = evaluateConditions(
                        allConditions,
                        newAnswers,
                        matrixData,
                        product.configGroup,
                        qtyValue,
                        true
                    );
                }
            } while (hasNewDefaults);

            processActionVariables(
                pageGenModel.actionVariables,
                newAnswers,
                matrixData,
                product.configGroup,
                qtyValue
            );

            // Reconcile stale option answers (mirrors handleAnswer)
            if (reconcileInvalidOptionAnswers(
                pageGenModel.questions,
                newAnswers,
                conditionsEvalResult
            )) {
                conditionsEvalResult = evaluateConditions(
                    allConditions,
                    newAnswers,
                    matrixData,
                    product.configGroup,
                    qtyValue,
                    true
                );
            }

            // 4) Final conditions into state and answers update
            setConditionEvaluations(conditionsEvalResult);
            setAnswers(newAnswers);
        }

        // 5) Compute pricing
        const hasAnswers = newAnswers && Object.keys(newAnswers).length > 0;
        
        if (hasAnswers) {
            const pricingModel = calculatePrice(
                pfLocal,
                matrixData,
                newAnswers,
                product,
                qtyValue,
                pricingFlags
            );
            
            const {
                configuredPrice: nextConfiguredPrice,
                configuredWholesalePrice: nextConfiguredWholesalePrice,
                configuredTotalDiscount: nextConfiguredTotalDiscount,
                adjustedUpcharges
            } = buildPricingOutput(pricingModel, pricingFlags, product);

            setUpcharges(adjustedUpcharges);
            setConfiguredPrice(nextConfiguredPrice);
            setConfiguredNetPrice(qtyValue * nextConfiguredPrice);
            setConfiguredWholesalePrice(nextConfiguredWholesalePrice);
            setConfiguredTotalDiscount(nextConfiguredTotalDiscount);
        }
    };

    // Initialize when configID or cloneID is present
    const initializeFromConfigOrClone = async (configID, cloneID, pricing) => {
        const idToLoad = !isEmpty(configID) ? configID : cloneID;
        const configResponse = await axios.get(
            `${api_url}Product/GetProductConfigurationById?cId=${idToLoad}&pr=${pricing}`
        );
        const productConfig = configResponse.data;
        let { product, sessionConfigurationDetails } = productConfig;
        const productResponse = await axios.get(
            `${api_url}Product/GetProductById`,
            { params: { c: ecn, id: product.productId, wh: ewh } }
        );
        product = productResponse.data;
        if (!product) {
            toast.error('Invalid product configuration');
            setIsLoading(false);
            return false;
        }

        setSelectedProduct(product);
        const productCategory = categories.find((x) => x.categoryId === product.categoryId);
        if (!productCategory) {
            toast.error('Category of selected product not found or is inactive');
            setIsLoading(false);
            return false;
        } else {
            setReadOnly(true);
            setDropDownCategories([productCategory]);
            setSelectedCategory(productCategory);
        }

        // In clone flow, keep current configId at 0 so a new record is created upon save
        if (!isEmpty(cloneID)) {
            configHeader.current.configId = 0;
        }

        // Map project and currency
        const resp = await axios.get(
            `${api_url}Product/GetProjectsByCustomer?c=${encodeURIComponent(ecn)}`
        );
        const customerProjects = resp.data;
        setProjects(customerProjects);
        const selectedProject = customerProjects.find((p) => p.id === productConfig.idProject);
        if (!selectedProject || String(selectedProject.customerNumber) !== String(configHeader.current.customerNo)) {
            setSelectedProjectOption(undefined);
            toast.warn('Original project was not found. Select an existing project or create a new one.');
        } else {
            setSelectedProjectOption({ label: selectedProject.projectName, value: selectedProject.id });
        }
        configHeader.current.currency = sessionConfigurationDetails.find((x) => x.configDetailVariable === 'IV_CUST_CURRNCY').answerText;

        // Build answers and core fields
        const answersFromConfig = buildAnswersFromSessionDetails(sessionConfigurationDetails);
        setIsPrivate(productConfig.isPrivate);
        setTitle(productConfig.title);
        setNotes("");
        setQty(productConfig.qty);

        // Permissions and pricing preset
        checkPricingPermissions(pricing, product, productConfig.price);

        // Apply answers and compute state
        await applyAnswersAndComputeState(product, answersFromConfig, productConfig.qty, queryParams.pricing);

        return true;
    };

    // function to create new project
    const createProject = async (projectName) => {
        if(!isGuest)
        {
            try {
                const normalizedProjectName = String(projectName ?? '').trim();
                if (!normalizedProjectName) {
                    toast.error('Please enter a valid project name.');
                    return null;
                }
            
                const payload = {
                    projectName: normalizedProjectName,
                    customerNumber: configHeader.current.customerNo,
                    createdBy: configHeader.current.email,
                    status: 'New',
                };
                const apiResponse = await axios.post(
                    `${api_url}Product/SaveProject`,
                    payload,
                    {
                        headers: { 'Content-Type': 'application/json' },
                    }
                );
                const newProject = apiResponse.data;
                return newProject;
            } catch (error) {
                console.log(`Could not create project ${projectName}`);
                toast.error(`Could not create project ${projectName}`);
                return null;
            }
        }
        return null;
        
    };



    // load initial state
    useEffect(() => {
        const { productID, configID, cloneID, pricing, isGuest } = queryParams;
        // Get returnUrl from query parameters
        const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
        
        // Store the return URL if it exists
        if (returnUrl) {
            setReturnToDashboard(decodeURIComponent(returnUrl));
            console.log("Return URL captured:", decodeURIComponent(returnUrl));
        }

        setEncryptedWarehouse(ewh);
        setEncryptedCustNo(ecn);
        setIsGuest(isGuest);
        const populateState = async () => {
            var answerGiven = false;
            if (isInitialStateLoaded.current === true) return;
            isInitialStateLoaded.current = true;
            setIsLoading(true);
            
            // fetch customer projects
            const resp = await axios.get(
                `${api_url}Product/GetProjectsByCustomer?c=${encodeURIComponent(ecn)}`
            );
            const customerProjects = resp.data;
            setProjects(customerProjects);
        
                if (!isEmpty(configID) || !isEmpty(cloneID)) {
                    configHeader.current.configId = toNumericConfigId(configID);
                    await initializeFromConfigOrClone(configID, cloneID, pricing);
                }
            
           else  if (!isEmpty(productID)) {
                // set selected product and category
                const productResponse = await axios.get(
                    `${api_url}Product/GetProductById`,
                    { params: { c: ecn, id: productID, wh: ewh } }
                );
 
                const product = productResponse.data;
                if (!product) {
                    toast.error('Invalid product sku');
                    setIsLoading(false);
                    return;
                }
                else{
                    setReadOnly(true);
                }
                setSelectedProduct(product);

                const productCategory = categories.find(
                    (x) => x.categoryId === product.categoryId
                );
                if (!productCategory) {
                    toast.error(
                        'Category of selected product not found or is inactive'
                    );
                    setIsLoading(false);
                    return;
                }
                setSelectedCategory(productCategory);
                
                await handleProductChange(product);
          
                checkPricingPermissions(pricing, product, 0);
            }

            setIsLoading(false);
        };

        populateState();
    }, [
        api_url,
        queryParams,
        categories,
        matrixData,
        handleProductChange,
    ]);

    function checkPricingPermissions(pricing, product, configPrice)
    {
        let tempConfigPrice = configPrice;

        if(product){
            setMSRPPrice(product.msrp);
            setWholesalePrice(product.wholeSalePrice);
            setMyProductPrice(product.basePrice);
    
            if(pricing.length > 0)
            {
                var pf = pricing.split(",");
    
                if(pf[2] === "1")
                {
                    setShowMyProductPrice(true);
                    tempConfigPrice = configPrice === "TBD" ? "TBD" : product.basePrice;
                  
                }
    
                if(pf[1] === "1")
                {
                    setShowWholeSalePrice(true);
                    if(pf[2] === "0")
                        tempConfigPrice = configPrice === "TBD" ? "TBD" : product.wholeSalePrice; 
                }
                else{
                    setShowWholeSalePrice(false);
                }
    
                if(pf[0] === "1")  //Show MSRP
                {
                    setShowMSRPPrice(true);
                    if(pf[2] === "0" && pf[1] === "0")
                    tempConfigPrice = configPrice === "TBD" ? "TBD" : product.msrp; 
                }
                else{
                    setShowMSRPPrice(false);
                }
                 
                
            }
    
            else{
                setMSRPPrice(true);
                setWholesalePrice(true);
                setShowMyProductPrice(true);
            }
        }

        if(configPrice !== "TBD" && configPrice !== 0)
        {
            setConfiguredPrice(configPrice);
            setConfiguredNetPrice(qty * configPrice);
        }
 
        else{
            if(tempConfigPrice !== "TBD")
            {
                setConfiguredPrice(tempConfigPrice);
                setConfiguredNetPrice(qty * tempConfigPrice);
            }
            
            else{
                setConfiguredPrice("TBD");
                setConfiguredNetPrice("TBD");
            }
        }
       
    }



    return (
        <div>
            <Card>
                
                    <TitleHeader show={false}></TitleHeader>
          
                <CardBody
                    style={{ minHeight: '85vh', backgroundColor: '#e9ecee' }}
                >
                    {isLoading && <Spinner />}
                    <Row>
                        <Col>
                            <Card className='innerForm'>
                                <CardBody
                                    style={{
                                        backgroundColor: '#fffff',
                                        boxShadow:
                                            'rgba(0, 0, 0, 0.1) 0px 12px 24px 0px ',
                                    }}
                                >
                                    <Row>
                                        <Col md='9'>
                                            <h3 className='qContainerTitle'>Configure Product</h3>
                                        </Col>
                                        <Col md='3'>
                                            <FormGroup check>
                                                <Input
                                                    id="chkPrivate"
                                                    type="checkbox"
                                                    onChange={ ()=>{
                                                        setIsPrivate(!isPrivate);
                                                    }}
                                                    />
                                                    {' '}
                                                    <Label check>
                                                    Make Private Only
                                                    </Label>
                                                </FormGroup>
                                        </Col>
                                    </Row>
                                   
                                    <Row>
                                        {/* categories */}
                                        <Col md='6'>
                                            <FormGroup>
                                                <CategoriesDropdown
                                                    onSelect={
                                                        handleCategoryChange
                                                    }
                                                    categories={dropDownCategories}
                                                    selectedCategory={
                                                        selectedCategory
                                                    }
                                                    isReadOnly={readOnly}
                                                />
                                            </FormGroup>
                                        </Col>
                                        {/* products */}
                                        {selectedCategory && (
                                            <Col md='6'>
                                                <FormGroup>
                                                    <ProductsDropdown
                                                        category={
                                                            selectedCategory
                                                        }
                                                        onSelect={
                                                            handleProductChange
                                                        }
                                                        selectedProduct={
                                                            selectedProduct
                                                        }
                                                        customerNumber={encryptedCustNo}
                                                        isReadOnly={readOnly}
                                                        warehouse={encryptedWarehouse}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>
                                    {
                                        !isGuest && (
                                            <Row>
                                            {/* project */}
                                            <Col md='6'>
                                                <FormGroup>
                                                    <Label> 
                                                        <span className='requiredQ '>
                                                            Project
                                                        </span>
                                                        <span  id="project_q" className='tooltipIcon'><i className="info">i</i>
                                                        </span>
                                                        <Tooltip
                                                            placement="right"
                                                            isOpen={projectTooltipOpen}
                                                            target={`project_q`}
                                                            toggle={toggleProjectTooltip}
                                                            >
                                                            Use Project Name or ID to organize configurations in My Configured Products
                                                            </Tooltip>
                                                            
                                                    </Label>
                                                    <CreatableSelect
                                                        isClearable
                                                        isDisabled={isLoading }
                                                        onChange={
                                                            handleProjectChange
                                                        }
                                                        options={projects.map(
                                                            (p) => ({
                                                                label: p.projectName,
                                                                value: p.id,
                                                            })
                                                        )}
                                                        value={
                                                            selectedProjectOption
                                                        }
                                                        placeholder='Select or Enter Project'
                                                        styles={
                                                            projectDropdownStyles
                                                        }
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {/* title */}
                                            <Col md='6'>
                                                <FormGroup>
                                                    <Label>
                                                        <span  className='requiredQ'>
                                                            Project Zone/Location
                                                        </span>
                                                        <span id="projectzone_q" className='tooltipIcon'>
                                                        <i className="info">i</i>
                                                        </span>
                                                        <Tooltip
                                                            placement="right"
                                                            isOpen={projectZoneTooltipOpen}
                                                            target={`projectzone_q`}
                                                            toggle={toggleProjectZoneTooltip}
                                                            >
                                                            Include Project Zone/Location for packing lists and to organize configurations in My Configured Products.
                                                            </Tooltip>
                                                    </Label>
                                                    <input
                                                        type='text'
                                                        name='title'
                                                        value={title}
                                                        className='custom-select'
                                                        placeholder='Enter title'
                                                        onChange={(event) =>
                                                            setTitle(
                                                                event.target.value
                                                            )
                                                        }
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        )
                                    }
                                   
                                    <br />
                                    <Row>
                                        {/* questions */}
                                        <Col md='6'>
                                            {selectedProduct && (
                                                <>
                                                    <small
                                                        style={{ color: 'red' }}
                                                    >
                                                        * denotes a required
                                                        question
                                                    </small>
                                                    {/* removed from Question component isRequired={isQuestionRequired(questionObj)} */}
                                                    {
                                                         
                                                        questions && (
                                                            <>
                                                            {questions.map((questionObj) => (
                                                                <Question
                                                                    key={`${selectedProduct.productId}_${questionObj.question.variable}`}
                                                                    product={selectedProduct}
                                                                    questionObj={questionObj}
                                                                    selectedAnswer={answers[questionObj.question.variable]}
                                                                   
                                                                    isRequired={
                                                                        questionObj.conditions.length > 0
                                                                          ? conditionEvaluations[questionObj.question.conditionName]
                                                                          : true
                                                                    }
                                                                    isVisible={questionObj.conditions.length > 0
                                                                        ? conditionEvaluations[questionObj.question.conditionName]
                                                                        : true
                                                                    }
                                                                    conditionEvaluations={conditionEvaluations}
                                                                    onAnswerGiven={handleAnswer}
                                                                />
                                                            ))}
                                                            </>
                                                        )
                                                    }
                                                    
                                                </>
                                            )}
                                            {/* notes */}
                                            {/* {selectedProduct && (
                                                <Row
                                                    style={{
                                                        marginTop: '20px',
                                                    }}
                                                >
                                                    <Col
                                                        md='12'
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Notes
                                                    </Col>
                                                    <Col md='12'>
                                                        <textarea
                                                            rows={3}
                                                            value={notes}
                                                            onChange={(e) =>
                                                                setNotes(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className='form-control'
                                                            placeholder='Enter your notes'
                                                            wrap='soft'
                                                            name='notes'
                                                            maxLength={1000}
                                                        />
                                                    </Col>
                                                </Row>
                                            )} */}

                                            <br />
                                            {/* price */}
                                           {selectedProduct && (
                                                <>
                                                   {showMSRPPrice && 1===2 && (
                                                        <Row>
                                                            <Col md='6'>
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px' }}>
                                                                    STARTING MSRP:
                                                                </b>
                                                            </Col>
                                                            <Col md='5'>
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px', textAlign: 'right' }}>
                                                                    <PriceElement price={msrpPrice} show={showMSRPPrice} currency={configHeader.current.currency}/>
                                                                </b>
                                                            </Col>
                                                        </Row>
                                                   )}

                                                   {showWholeSalePrice && 1===2 && (
                                                        <Row>
                                                            <Col md='6'>
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px' }}>
                                                                    STARTING WHOLESALE:
                                                                </b>  
                                                            </Col>
                                                            <Col md='5'>    
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px', textDecoration:'line-through', textAlign: 'right' }}>
                                                                    <PriceElement price={wholesalePrice} show={showWholeSalePrice} currency={configHeader.current.currency}/>
                                                                </b>
                                                            </Col>
                                                        </Row>      
                                                   )}                          

                                                   {showMyProductPrice && !isGuest && (
                                                        <Row>
                                                            <Col md='6'>
                                                                <b style={{ color: 'rgb(24, 187, 156)', fontSize: '14px' }}>
                                                                    STARTING MY PRICE: 
                                                                </b>
                                                            </Col>
                                                            <Col md='5' style={{textAlign: 'right'}}>
                                                                <b style={{ color: 'rgb(24, 187, 156)', fontSize: '14px' }}>
                                                                    <PriceElement price={myProductPrice} show={showMyProductPrice} currency={configHeader.current.currency}/>
                                                                </b>
                                                            </Col>
                                                        </Row>
                                                   )}

                                                   
                                                       
                                                   {showWholeSalePrice && configuredWholesalePrice > 0 && !isGuest&& (     
                                                        <Row>
                                                            <Col md='6'>
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px' }}>
                                                                    CONFIGURED WHOLESALE: 
                                                                </b>
                                                            </Col>
                                                            <Col md='5' style={{textAlign: 'right'}}>
                                                                <b style={{ color: 'rgb(0, 0, 0)', fontSize: '14px' }}>
                                                                    <PriceElement price={configuredWholesalePrice} show={true} currency={configHeader.current.currency}/>
                                                                </b>
                                                            </Col>
                                                        </Row>
                                                   )}
                                            
                                                   {showMyProductPrice && selectedProduct.dfi > 0 && !isGuest && (
                                                        <Row>
                                                            <Col md='6'>
                                                                <b style={{ color: '#007DAB', fontSize: '14px' }}>
                                                                    TOTAL PROGRAM SAVINGS: 
                                                                </b>
                                                            </Col>
                                                            <Col md='5' style={{textAlign: 'right'}}>
                                                                <b style={{ color: '#007DAB', fontSize: '14px'}}>
                                                                    <PriceElement price={configuredTotalDiscount} show={true} currency={configHeader.current.currency}/>
                                                                </b>
                                                            </Col>
                                                        </Row> 
                                                   )}
                                                   {showMyProductPrice && !isGuest && (
                                                    <Row>
                                                        <Col md='6'>
                                                            <b style={{ color: 'rgb(24, 187, 156)', fontSize: '14px' }}>
                                                                CONFIGURED MY PRICE: 
                                                            </b>
                                                        </Col>
                                                        <Col md='5' style={{textAlign: 'right'}}>
                                                            <b style={{ color: 'rgb(24, 187, 156)', fontSize: '14px'}}>
                                                                <PriceElement price={configuredPrice} show={true} currency={configHeader.current.currency}/>
                                                            </b>
                                                        </Col>
                                                   </Row>
                                                   )}
                                                   
        {
                                                    upcharges && upcharges.length > 0  && !isGuest  && (
                                                        <>
                                                           <Row style={{ margin: '15px 0px', borderTop: '2px solid #000' }}></Row>
                                                            <Row onClick={() => setShowUpcharges(!showUpcharges)} style={{ cursor: 'pointer'}}>
                                                                <Col md='6'>
                                                                    <b style={{ color: '#000', fontSize: '14px' }}>
                                                                        PRICE DETAIL {showUpcharges ? '▼' : '▶'}
                                                                    </b>
                                                                </Col>
                                                                <Col md='5'>
                                                                   
                                                                </Col>
                                                            </Row>
                                                            {showUpcharges   && (
                                                                <Row>
                                                                     
                                                                    <Col md='12'>
                                                                    {
                                                                            upcharges.map((upch, idx) => (
                                                                                <Row key={`upc_${idx}`} style={{ color: 'rgb(0, 0, 0)', fontSize: '14px', alignItems: 'flex-start' }}>
                                                                                   <Col md='7' style={{ textAlign: 'left', paddingRight: '8px', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}> <b>{upch.description}</b>{": "}</Col>
                                                                                   <Col md='4' style={{ textAlign: 'right', paddingLeft: '8px', whiteSpace: 'nowrap' }}> <PriceElement price={Number(upch.amount)} show={true} currency={configHeader.current.currency}/> </Col>
                                                                                </Row>
                                                                            ))
                                                                        }
                                                                       
                                                                    </Col>
                                                                </Row>
                                                            )}
                                                        </>
                                                    )
                                                   }
                                                               
                                                   
                                                   {configuredPrice === "TBD" && (
                                                        <p style={{"marginTop": "15px"}}>
                                                            <b>PLEASE NOTE</b>: Your custom configuration request will need to be emailed to the James Engineering Team and is currently <b>Under Design Review</b>. You'll be notified by email upon design, drawing and pricing approval. Please partner with your Inside Sales Representative to place this order manually.
                                                        </p>
                                                   )}
                                                </>
                                            )}
                                            <br />
                                            {/* Save and Add to Cart etc. buttons */}
                                            
                                            {selectedProduct && !isGuest  &&    (
                                                <>
                                                <Row>
                                                    <Col md='8'>
                                                    {
                                                    queryParams.source !== 'cart' && configHeader.current.source !== 'sf' && configHeader.current.source !== 'salesforce' && (
                                                        <Button
                                                            className='btn-custom mt-2'
                                                            color='primary'
                                                            style={{
                                                                float: 'left',
                                                                marginBottom:
                                                                    '10px',
                                                                marginRight:
                                                                    '10px',
                                                            }}
                                                            onClick={
                                                                handleSaveButtonClick
                                                            }
                                                        >
                                                            Save
                                                        </Button>
                                                    )}
                                                        <AddtoCartButton
                                                            src={configHeader.current.source}
                                                            canAddToCart={
                                                                !customAnswerGiven &&  queryParams.canAddToCart
                                                            }
                                                            qty={qty}
                                                            onChangeQty={
                                                                handleQtyChange
                                                            }
                                                            onSave={
                                                                handleSaveButtonClick
                                                            }
                                                            onSendEmail={
                                                                handleSaveButtonClick
                                                            }
                                                           
                                                        />
                                                       
                                                    </Col>
                                                    
                                                </Row>

                                                <Row>
                                                <Col md='4' style={{
                                                                marginTop: '20px' 
                                                             
                                                            }}>
                                                    <b
                                                            style={{
                                                                color: '#007DAB',
                                                                fontSize:
                                                                    '14px',
                                                            }}
                                                        >
                                                            EXT. NET PRICE:
                                                            </b>
                                                      
                                                    </Col>
                                                    <Col md='5' style={{
                                                                marginTop: '20px' 
                                                             
                                                            }}>
                                                    <b
                                                            style={{
                                                                color: '#007DAB',
                                                                fontSize:
                                                                    '14px',
                                                            }}>
                                                         <PriceElement price={configuredNetPrice} show={true}  currency={configHeader.current.currency}/>
                                                            
                                                        </b>
                                                        
                                                   </Col>
                                                </Row>
                                                </>
                                            )}
                                        </Col>
                                        {/* product image */}
                                        <Col md='6'>
                                            {selectedProduct && selectedProduct.heroImg && (
                                                <>
                                                    <img
                                                        src={selectedProduct.heroImg}
                                                        alt={selectedProduct.description}
                                                    />
                                                    <div className='heroImgCaption'>
                                                        {selectedProduct.heroImgCaption}
                                                    </div>
                                                </>
                                            )}

                                            {selectedProduct && selectedProduct.productNote && (
                                                <>
                                                    <ProductNoteHtml htmlContent={selectedProduct.productNote} />
                                                </>
                                            )}

                                            {selectedProduct && selectedProduct.customImg && (
                                                <>
                                                    <Carousel
                                                        interval={null}
                                                        wrap={true}
                                                        style={{ marginTop: "25px" }}
                                                        className="custom-carousel"
                                                    >
                                                        {selectedProduct.customImg
                                                            .split('||')
                                                            .map(imgSrc => imgSrc.trim())
                                                            .filter(imgSrc => imgSrc) // remove empty strings
                                                            .map((imgSrc, index) => (
                                                                <Carousel.Item key={imgSrc + index}>
                                                                    <img
                                                                        src={imgSrc}
                                                                        alt={selectedProduct.description}
                                                                        height={300}
                                                                        style={{ objectFit: 'contain' }}
                                                                    />
                                                                </Carousel.Item>
                                                            ))
                                                        }
                                                    </Carousel>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <style jsx>{`
                .custom-carousel .carousel-control-prev-icon,
                .custom-carousel .carousel-control-next-icon {
                    background-color: rgba(128, 128, 128, 0.5);
                    border-radius: 50%;
                    padding: 20px;
                }
                .custom-carousel .carousel-control-prev,
                .custom-carousel .carousel-control-next {
                    width: 10%;
                }
            `}</style>
        </div>
    );
};

 


export default QuestionsContainer;

/// helper functions
function getConditionsForQuestions(questionObjList) {
    let dependentConditions = {};

    for (let questionObj of questionObjList) {
        // populate rules object with rule used in the question
        if (!isEmpty(questionObj.question.conditionName)) {
            dependentConditions[questionObj.question.conditionName] = questionObj.conditions;
        }

        // First check if constrainedOptions exists and has elements
        /*const optionsToCheck = (questionObj.constrainedOptions && questionObj.constrainedOptions.length > 0) 
            ? questionObj.constrainedOptions 
            : questionObj.options;
*/
         
        if(questionObj.constrainedOptions2 && questionObj.constrainedOptions2.length>0)
        {
                for(let conOption of questionObj.constrainedOptions2)
                {
              
                    if (!isEmpty(conOption.conditionName)) {
                            dependentConditions[conOption.conditionName] = conOption.conditions;
                    }
                }
        }
        
            // Iterate through the selected options list (either constrained or regular)
        for (let option of questionObj.options) {
            // populate rules object with rules used in the option
            if (!isEmpty(option.conditionName)) {
                dependentConditions[option.conditionName] = option.conditions;
            }
        }
        
        
    }
    
    return dependentConditions;
}

 

// function to evaluate given rule records
function evaluateConditions(conditionRecords, answersObj, matrixData, configGroup, qty , flipSign) {
    const evaluationResult = {};
 
    for (const [conditionName, conditions] of Object.entries(conditionRecords)) {
        evaluationResult[conditionName] = EvaluateCondition(
            conditions,
            answersObj,
            matrixData,
            configGroup,
            qty,
            false,
            flipSign
        );
    }
    return evaluationResult;
}

// checks if the answer given by user is a custom answer
function isCustomAnswer(answer, visibleOptions) {
   
    const customOption = visibleOptions.find(
        (option) => option.isCustomOption 
    );
    if (customOption && answer === customOption.optionValue) {
        return true;
    }
    return false;
}

// project dropdown styles
const projectDropdownStyles = {
    clearIndicator: (base) => ({
        ...base,
        color: '#7e7e7e',
        '&:hover': { color: '#7e7e7e' },
    }),
    container: (base, { isFocused }) => ({
        ...base,
        background: 'rgb(0, 125, 171)',
        height: 'calc(2.25rem + 2px)',
        width: '100%',
        border: '1px solid #ced4da',
        borderRadius: 0,
        borderColor: isFocused ? '#80bdff' : '#ced4da',
        boxShadow: isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
    }),
    control: (base) => ({
        ...base,
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        padding: '0px 2px 0px 10px',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#fff',
        backgroundColor: 'transparent',
        '&:hover': { color: '#7e7e7e' },
        padding: '0px',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#fff',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        '& > span': { display: 'none' },
    }),
    singleValue: (base) => ({
        ...base,
        color: '#fff',
    }),
    input: (base) => ({
        ...base,
        color: '#fff',
    }),
    valueContainer: (base) => ({
        ...base,
        paddingLeft: '0px',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 5,
        borderRadius: 0,
        backgroundColor: 'rgb(235, 235, 235)',
        border: '1px solid #ced4da',
        margin: '0px 0px',
        boxShadow: 'none',
    }),
    menuList: (base) => ({
        ...base,
        background: 'rgb(235, 235, 235)',
        color: '#7e7e7e',
    }),
    option: (base, { isSelected }) => ({
        ...base,
        backgroundColor: isSelected ? '#1266d8' : 'transparent',
        color: isSelected ? '#ffffff' : '#7e7e7e',
        paddingTop: '0px',
        paddingBottom: '0px',
        '&:hover': { backgroundColor: '#1266d8', color: '#ffffff' },
    }),
};
